import React, { useState } from "react";
import PropTypes from "prop-types";
import "./RankedList.scss";

const defaultFormat = (n) => Number(n || 0).toLocaleString("en-GB");

/**
 * RankedList — a breakdown as aligned rows: label, a bar, the figure, and any number of
 * extra columns under real headers.
 *
 * Why not HorizontalBar for this: its track shrinks to make room for whatever trails it,
 * so a row with "13 quoted" beside it has a shorter bar than the row above — the lengths
 * stop being comparable, which is the one thing a bar is for. Here every row shares one
 * grid, so bars, figures and columns line up down the list.
 *
 * Rows are drawn in the order given: sort them for a ranking, or pass buckets in their
 * natural order (price bands, time spent) for a sideways histogram.
 *
 * @param rows     [{ key, label, sub?, value, color?, title?, cells?: {colKey: node}, muted?, onClick? }]
 * @param columns  extra columns after the figure: [{ key, label, width?, align?, help? }]
 * @param total    when given, the share of it is shown after each figure
 * @param limit    rows shown before "Show all N"
 */
const RankedList = ({
  rows,
  columns = [],
  labelHeader,
  valueHeader,
  format = defaultFormat,
  max,
  total,
  limit = 0,
  color,
  labelWidth = "minmax(0, 1.5fr)",
  barWidth = "minmax(48px, 1fr)",
  emptyText = "Nothing in this period.",
  activeKey,
  className = "",
}) => {
  const [all, setAll] = useState(false);
  if (!rows || rows.length === 0) return <p className="ranked-list__empty">{emptyText}</p>;
  const top = max || Math.max(1, ...rows.map((r) => r.value || 0));
  const shown = limit && !all ? rows.slice(0, limit) : rows;
  const extras = columns.map((c) => c.width || "minmax(52px, auto)");
  const template = [labelWidth, barWidth, "minmax(44px, auto)", total ? "40px" : null, ...extras].filter(Boolean).join(" ");
  // In a narrow container the bar and the share go first, so the names keep their room.
  const narrow = [labelWidth, "minmax(40px, auto)", ...extras].join(" ");
  const headed = Boolean(labelHeader || valueHeader || columns.some((c) => c.label));
  return (
    <div className={`ranked-list ${className}`} style={{ "--rl-template": template, "--rl-template-narrow": narrow }}>
      {headed && (
        <div className="ranked-list__row ranked-list__row--head" aria-hidden="true">
          <span>{labelHeader}</span>
          <span className="ranked-list__track-head" />
          <span className="ranked-list__num">{valueHeader}</span>
          {total ? <span className="ranked-list__share-head" /> : null}
          {columns.map((c) => (
            <span key={c.key} className={c.align === "left" ? "" : "ranked-list__num"} title={c.help}>{c.label}</span>
          ))}
        </div>
      )}
      {shown.map((r) => {
        const pctOfMax = top ? Math.max(((r.value || 0) / top) * 100, r.value ? 1.2 : 0) : 0;
        const Tag = r.onClick ? "button" : "div";
        return (
          <Tag
            key={r.key}
            type={r.onClick ? "button" : undefined}
            className={`ranked-list__row${r.muted ? " ranked-list__row--muted" : ""}${r.onClick ? " ranked-list__row--clickable" : ""}${activeKey && activeKey === r.key ? " ranked-list__row--active" : ""}`}
            onClick={r.onClick}
            title={r.title}
          >
            <span className="ranked-list__label">
              <span className="ranked-list__name">
                {r.swatch && <i className="ranked-list__swatch" style={{ background: r.swatch }} />}
                {r.label}
              </span>
              {r.sub && <span className="ranked-list__sub">{r.sub}</span>}
            </span>
            <span className="ranked-list__track">
              <span className="ranked-list__bar" style={{ width: `${pctOfMax}%`, background: r.color || color || undefined }} />
            </span>
            <span className="ranked-list__num ranked-list__value">{format(r.value)}</span>
            {total ? <span className="ranked-list__num ranked-list__share">{total ? `${Math.round(((r.value || 0) / total) * 100)}%` : ""}</span> : null}
            {columns.map((c) => (
              <span key={c.key} className={c.align === "left" ? "ranked-list__cell" : "ranked-list__num ranked-list__cell"}>{r.cells ? r.cells[c.key] : null}</span>
            ))}
          </Tag>
        );
      })}
      {limit > 0 && rows.length > limit && (
        <button type="button" className="ranked-list__more" onClick={() => setAll(!all)}>
          {all ? "Show fewer" : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
};

RankedList.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    sub: PropTypes.node,
    value: PropTypes.number,
    color: PropTypes.string,
    swatch: PropTypes.string,
    title: PropTypes.string,
    cells: PropTypes.object,
    muted: PropTypes.bool,
    onClick: PropTypes.func,
  })).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired, label: PropTypes.node, width: PropTypes.string, align: PropTypes.string, help: PropTypes.string,
  })),
  labelHeader: PropTypes.node,
  valueHeader: PropTypes.node,
  format: PropTypes.func,
  max: PropTypes.number,
  total: PropTypes.number,
  limit: PropTypes.number,
  color: PropTypes.string,
  labelWidth: PropTypes.string,
  barWidth: PropTypes.string,
  emptyText: PropTypes.string,
  activeKey: PropTypes.string,
  className: PropTypes.string,
};

export default RankedList;
